import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * 1DELUXE CRO: cart upsell add-to-cart and the drawer's sticky checkout bar.
 * Markup: snippets/dlx-cart-upsell.liquid and snippets/dlx-upsell-card.liquid.
 *
 * Adds go through the Cart AJAX API with the same section-rendering contract the theme
 * uses (every cart-items-component section is re-rendered), then a CartAddEvent lets the
 * cart drawer, cart page and cart icon update themselves.
 */

/** @type {IntersectionObserver | undefined} */
let stickyObserver;

/**
 * @returns {string[]} Unique section IDs of every cart-items-component on the page.
 */
function cartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/**
 * Reads the cart item count from rendered section HTML, falling back to /cart.js.
 * @param {Record<string, string>} sections
 * @returns {Promise<number>}
 */
async function readItemCount(sections) {
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const countElement = doc.querySelector('[ref="cartItemCount"]');
    if (countElement) return parseInt(countElement.textContent || '0', 10) || 0;
  }

  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  const cart = await response.json();
  return Number(cart.item_count) || 0;
}

/**
 * @param {HTMLButtonElement} button
 * @param {string} text
 */
function setButtonLabel(button, text) {
  const label = button.querySelector('[data-dlx-upsell-label]');
  if (label) label.textContent = text;
}

/**
 * @param {HTMLButtonElement} button
 */
async function addFromCard(button) {
  const card = button.closest('[data-dlx-upsell-card]');
  const root = button.closest('[data-dlx-upsell]');
  if (!(card instanceof HTMLElement) || !(root instanceof HTMLElement)) return;

  const select = card.querySelector('select');
  const errorElement = card.querySelector('[data-dlx-upsell-error]');
  const variantId = Number(select instanceof HTMLSelectElement ? select.value : card.dataset.variantId);

  if (!variantId) {
    // No size chosen yet: never guess a size for the shopper.
    if (select instanceof HTMLSelectElement) {
      select.classList.add('is-invalid');
      select.focus();
      try {
        select.showPicker?.();
      } catch (_) {
        // showPicker needs a user gesture and isn't supported everywhere; focus is enough.
      }
    }
    if (errorElement) {
      errorElement.textContent = root.dataset.choose || '';
      errorElement.removeAttribute('hidden');
    }
    return;
  }

  select?.classList.remove('is-invalid');
  const originalLabel = button.querySelector('[data-dlx-upsell-label]')?.textContent || '';

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  setButtonLabel(button, root.dataset.adding || originalLabel);
  errorElement?.setAttribute('hidden', '');

  try {
    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties: { _upsell: 'cart' } }],
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    /** @type {Record<string, string>} */
    const sections = data.sections || {};
    const itemCount = await readItemCount(sections);

    setButtonLabel(button, root.dataset.added || originalLabel);
    button.removeAttribute('aria-busy');

    button.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'dlx-cart-upsell',
        itemCount,
        variantId: String(variantId),
        sections,
      })
    );
  } catch (error) {
    console.error(error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    setButtonLabel(button, originalLabel);

    if (errorElement) {
      errorElement.textContent = root.dataset.error || '';
      errorElement.removeAttribute('hidden');
    }
  }
}

/**
 * @param {MouseEvent} event
 */
function onClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-dlx-upsell-add]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  addFromCard(button);
}

/**
 * Shows the drawer's sticky checkout bar only while the real checkout buttons are out of view.
 */
function bindStickyCheckout() {
  stickyObserver?.disconnect();
  stickyObserver = undefined;

  const sticky = document.querySelector('[data-dlx-sticky-cta]');
  if (!(sticky instanceof HTMLElement) || !('IntersectionObserver' in window)) return;

  const root = sticky.closest('.cart-drawer__content');
  const ctas = root?.querySelector('.cart__ctas');
  const bar = sticky.querySelector('.dlx-sticky-cta__bar');
  if (!(root instanceof HTMLElement) || !ctas || !(bar instanceof HTMLElement)) return;

  stickyObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      const show = !entry.isIntersecting;
      sticky.classList.toggle('is-visible', show);
      bar.inert = !show;
    },
    { root, threshold: 0 }
  );
  stickyObserver.observe(ctas);
}

function onCartUpdate() {
  // The cart sections morph after this event; bind to the updated DOM.
  setTimeout(bindStickyCheckout, 0);
  setTimeout(bindStickyCheckout, 500);
}

/**
 * Clears the size prompt once a size is picked.
 * @param {Event} event
 */
function onChange(event) {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.closest('[data-dlx-upsell-card]')) return;
  select.classList.remove('is-invalid');
  select.closest('[data-dlx-upsell-card]')?.querySelector('[data-dlx-upsell-error]')?.setAttribute('hidden', '');
}

if (!window.__dlxCartUpsell) {
  window.__dlxCartUpsell = true;
  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindStickyCheckout, { once: true });
  } else {
    bindStickyCheckout();
  }
}
